"use client";

import React from "react";
import { ArrowRight, Phone, Mail, MapPin, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const nearbyAreas = [{
  name: "Reading",
  to: "/AreasWeCoverPage"
}, {
  name: "Ascot",
  to: "/AreasWeCoverPage"
}, {
  name: "Bracknell",
  to: "/AreasWeCoverPage"
}, {
  name: "Wokingham",
  to: "/AreasWeCoverPage"
}, {
  name: "Windsor",
  to: "/AreasWeCoverPage"
}, {
  name: "Maidenhead",
  to: "/AreasWeCoverPage"
}];
const trustPoints = ["NASC & CHAS accredited", "Direct contract manager support", "Detailed site surveys and pricing"];
export default function Quote() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="quotecta" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="relative overflow-hidden rounded-2xl bg-secondary text-secondary-foreground shadow-xl p-8 sm:p-12 lg:p-16">
          {/* Subtle structural background accents */}
          <div className="absolute top-0 right-0 -mt-8 -mr-8 h-64 w-64 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 -mb-8 -ml-8 h-64 w-64 rounded-full bg-primary/15 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left Column: Heading, Supporting copy, Trust markers */}
            <div className="lg:col-span-7 space-y-6">
              <Badge variant="outline" className="bg-secondary text-secondary-foreground border-border/40 px-3 py-1 text-xs font-semibold tracking-wide uppercase">
                Commercial & Residential Access
              </Badge>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-secondary-foreground">
                Request a scaffolding quote
              </h2>

              <p className="text-base sm:text-lg text-secondary-foreground/85 max-w-2xl leading-relaxed">
                Send us your project scope, drawings, or request an on-site survey. Our experienced estimating team provides clear, prompt, and safety-compliant estimates.
              </p>

              {/* Trust checklist */}
              <div className="pt-2 flex flex-col sm:flex-row flex-wrap gap-y-2 gap-x-6">
                {trustPoints.map((point, index) => <div key={point} data-index={index} className="flex items-center gap-2 text-xs sm:text-sm text-secondary-foreground/80 font-medium">
                    <ShieldCheck className="size-4 text-primary shrink-0" />
                    <span>{point}</span>
                  </div>)}
              </div>
            </div>

            {/* Right Column: Interactive CTA Card */}
            <div className="lg:col-span-5">
              <div className="rounded-xl bg-card text-card-foreground p-6 sm:p-8 shadow-lg border border-border space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    Get in touch today
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Choose your preferred way to speak with our contracts desk.
                  </p>
                </div>

                <div className="space-y-3">
                  {/* Primary Quote Page Action */}
                  <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 shadow-sm flex items-center justify-center gap-2">
                    <Link to="/ContactRequestQuotePage">
                      <span>Request a quote online</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>

                  <div className="relative py-1 flex items-center justify-center">
                    <Separator className="w-full" />
                    <span className="absolute bg-card px-2 text-xs text-muted-foreground uppercase font-medium tracking-wider">
                      Or contact directly
                    </span>
                  </div>

                  {/* Direct Phone Link */}
                  <a href="tel:01344929900" className="flex items-center justify-between p-3.5 rounded-lg border border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground transition-colors group">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-md bg-muted text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Phone className="size-4" />
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Call direct</div>
                        <div className="text-sm font-semibold text-foreground">01344 929900</div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-primary group-hover:translate-x-0.5 transition-transform">
                      Call now
                    </span>
                  </a>

                  {/* Direct Email Link */}
                  <a href="mailto:quotes@bmfscaffolding.co.uk" className="flex items-center justify-between p-3.5 rounded-lg border border-border bg-background hover:bg-accent hover:text-accent-foreground transition-colors group">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-md bg-muted text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Mail className="size-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs text-muted-foreground">Email drawings & specs</div>
                        <div className="text-sm font-semibold text-foreground truncate">
                          quotes@bmfscaffolding.co.uk
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-primary group-hover:translate-x-0.5 transition-transform">
                      Email
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Area: Nearby Areas Links */}
          <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-border/20 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-secondary-foreground/75">
            <div className="flex items-center gap-2 font-medium">
              <MapPin className="size-4 text-primary shrink-0" />
              <span>Nearby areas covered:</span>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {nearbyAreas.map((area, index) => <span key={area.name} data-index={index} className="flex items-center gap-4">
                  <Link to={area.to} className="text-secondary-foreground hover:text-primary transition-colors underline-offset-4 hover:underline font-medium">
                    {area.name}
                  </Link>
                  {index < nearbyAreas.length - 1 && <span className="text-secondary-foreground/30 select-none">•</span>}
                </span>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}